import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  API_Application,
  API_ApplicationDetail,
  API_GetCpfWithdrawalDetails,
  API_GetCpfWithdrawalOverview,
  API_GetNotifiedStatus,
  API_GetPreviousCpfSubmission,
  BASEURL_POLYMER,
  SUB_PATH_BASIC,
} from '../../utils/api';
import { SubmitDocuments } from '../../models/classes/CooSubmission.class';
import { CpfWithdrawalInfoOverview } from '../../models/interfaces/stage-e.interface';
import { PolymerCrypto } from '../../utils/encryptor';
import { StageDReviewApplication } from '../../models/classes/stage-d/Review-Application.class';

interface QueryParams {
  uinfin: string;
  singpass_token: string;
  application_id: string;
}

interface QueryParamsWithRequestId extends QueryParams {
  request_uuid: string[];
}

interface QueryParamsWithHdbRef extends QueryParams {
  hdb_ref: string;
}

interface HdbReferenceDto {
  uuid: string;
  hdb_ref: string;
  proposed_hdb_ref: string;
}

export const stageEGetDocument = createApi({
  reducerPath: 'stageEGetDocument',
  baseQuery: fetchBaseQuery({
    baseUrl: BASEURL_POLYMER,
    prepareHeaders: (headers, { getState }) => {
      headers.set('Access-Control-Request-Method', 'GET');
      headers.set('X-Content-Type-Options', 'nosniff');
    },
  }),
  // global keepUnusedDataFor configuration for the api
  keepUnusedDataFor: 1,
  endpoints: (builder) => ({
    getSubmittedDocuments: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_ApplicationDetail}?applicationUuid=${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      transformResponse: (response: any, meta, arg) => {
        return response.documents.filter((e: any) => {
          if (!e.label) return false;
          if (!/^DC\d+$/.test(e.code)) return false; // ensure code format
          const num = parseInt(e.code.slice(2), 10);
          return num >= 1 && num <= 30;
        });
      },
      keepUnusedDataFor: 1,
    }),

    getLegalDocuments: builder.query<SubmitDocuments[], QueryParams & { document_codes: string[] }>({
      query: (queryParams: QueryParams & { document_codes: string[] }) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/document?application_uuid=${queryParams.application_id}&document_codes=${queryParams.document_codes}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY: Get reference documents for Stage E (legacy - before Dec 16, 2025)
     * Uses /application/getApplicationUpdate endpoint
     */
    getReferDocuments: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/application/getApplicationUpdate/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      transformResponse: (response: any, meta, arg) => {
        const formatted_response = new StageDReviewApplication(response);
        if (formatted_response.stageEProcessFolders) {
          formatted_response.stageEProcessFolders();
        }
        if (formatted_response.json) {
          try {
            return formatted_response.json()?.folders[0]?.comments[0]?.documents ?? [];
          } catch (e) {
            console.log('STAGE E - REFER ERROR', e);
            return [];
          }
        }
        return response;
      },
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY ENTRY
     */
    getCpfServiceNotifiedStatus: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_GetNotifiedStatus}/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY: Retrieve CPF Withdrawal Overview from Polymer DB.
     */
    getCpfWithdrawalOverview: builder.query<CpfWithdrawalInfoOverview, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_GetCpfWithdrawalOverview}/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      transformResponse: (response: any) => {
        if (!response || !response.parties) {
          return response;
        }
        return {
          ...response,
          parties: response.parties.map((party: any) => ({
            ...party,
            nric: PolymerCrypto.decryption(party.nric),
            cpfAccountNo: PolymerCrypto.decryption(party.cpfAccountNo),
          })),
        };
      },
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY: Retrieve CPF Withdrawal Details from Polymer DB.
     */
    getCpfWithdrawalDetails: builder.query<any, QueryParamsWithRequestId>({
      query: (queryParams: QueryParamsWithRequestId) => ({
        method: 'GET',
        url: `${API_GetCpfWithdrawalDetails}/${queryParams.application_id}`,
        params: {
          request_uuids: queryParams.request_uuid,
        },
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      transformResponse: (response: any) => {
        if (!response || !response.parties) {
          return response;
        }
        return {
          ...response,
          parties: response.parties.map((party: any) => ({
            ...party,
            cpfAccountNo: PolymerCrypto.decryption(party.cpfAccountNo),
          })),
        };
      },
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY ENTRY
     */
    getPreviousCpfSubmission: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_GetPreviousCpfSubmission}/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),

    /**
     * QUERY ENTRY
     */
    getEndorseForms: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/application/getApplicationUpdate/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
    }),

    /**
     * QUERY: Get CompletionDay payment details
     */
    getCompletionPaymentDetails: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/payment/completion/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
    }),

    /**
     * QUERY: Get Financial Plan details
     */
    getFinancialPlan: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/getFinancialPlan/${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
    }),

    /**
     * QUERY: Get the PROPOSED HPS-coverage details.
     */
    proposedHpsCoverage: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/${queryParams.application_id}/owners/coverage/proposed`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
    }),

    /**
     * QUERY: Get the rental status of a HDB.
     */
    checkHdbExistingRental: builder.query<any, QueryParamsWithHdbRef>({
      query: (queryParams: QueryParamsWithHdbRef) => ({
        method: 'GET',
        url: `${SUB_PATH_BASIC}/hdbExistingRentalCheck?hdb_ref=${queryParams.hdb_ref}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
    }),

    getApplicationDetail: builder.query<any, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_ApplicationDetail}?applicationUuid=${queryParams.application_id}`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),

    getApplicationHdbRef: builder.query<HdbReferenceDto, QueryParams>({
      query: (queryParams: QueryParams) => ({
        method: 'GET',
        url: `${API_Application}/${queryParams.application_id}/hdb-ref`,
        headers: {
          uinfin: queryParams.uinfin,
          Authorization: `Bearer ${queryParams.singpass_token}`,
        },
      }),
      keepUnusedDataFor: 1,
    }),
  }),
});

export const {
  useCheckHdbExistingRentalQuery,
  useProposedHpsCoverageQuery,
  useGetFinancialPlanQuery,
  useGetCompletionPaymentDetailsQuery,
  useGetEndorseFormsQuery,
  useGetSubmittedDocumentsQuery,
  useGetLegalDocumentsQuery,
  useGetReferDocumentsQuery,
  useGetCpfServiceNotifiedStatusQuery,
  useGetCpfWithdrawalOverviewQuery,
  useGetCpfWithdrawalDetailsQuery,
  useGetPreviousCpfSubmissionQuery,
  useGetApplicationDetailQuery,
  useGetApplicationHdbRefQuery,
} = stageEGetDocument;
